// Universal credentials client. Post-credential-redesign (cloud-agent-
// backend migration 0010) one schema covers every provider: cloud, SSH,
// GitHub, GitLab, Azure DevOps, OpenAI, Anthropic. Provider-specific
// fields live inside `metadata`; the catalog at ./providers.ts is the
// authoritative shape per provider.
//
// Surface:
//   apiClient.credentials.list()           — every credential in the project
//   apiClient.credentials.create()         — one create endpoint, any provider
//   apiClient.credentials.update()         — partial update
//   apiClient.credentials.delete()         — remove
//   apiClient.credentials.providers()      — fetch the catalog
//   apiClient.credentials.sandbox.*        — sandbox associations (any provider)
//   apiClient.credentials.gatewayTokens.*  — sandbox FOA bearer tokens
//   apiClient.credentials.agentTokens.*    — sandbox MCP agent access tokens
import type { BaseClient } from '../../http/BaseClient.js';
import { AgentTokensClient } from './AgentTokensClient.js';
import { GatewayTokensClient } from './GatewayTokensClient.js';
import { TelegramClient } from './TelegramClient.js';
import {
  Credential,
  CreateCredentialRequest,
  ListCredentialsQuery,
  ProviderID,
  SandboxCredential,
  SimpleKeysResponse,
  UpdateCredentialRequest,
  WireSandboxCredential,
  normalizeSandboxCredential,
} from './types.js';
import type { ProviderSpec } from './providers.js';

// ── Sandbox associations ───────────────────────────────────────────────

class SandboxCredentialScope {
  constructor(private readonly http: BaseClient) {}

  async list(sandboxId: string): Promise<SandboxCredential[]> {
    const res = await this.http.get<{ success: boolean; items?: WireSandboxCredential[]; data?: WireSandboxCredential[] }>(
      `/sandbox/${sandboxId}/credential`,
    );
    const list = res.items ?? res.data ?? [];
    return list.map(normalizeSandboxCredential);
  }

  attach(sandboxId: string, credentialId: string): Promise<SimpleKeysResponse> {
    return this.http.post<SimpleKeysResponse>(`/sandbox/${sandboxId}/credential`, {
      body: { credentialId },
    });
  }

  detach(sandboxId: string, credentialId: string): Promise<SimpleKeysResponse> {
    return this.http.delete<SimpleKeysResponse>(`/sandbox/${sandboxId}/credential/${credentialId}`);
  }
}

// ── Top-level credentials client ──────────────────────────────────────

export class CredentialsClient {
  readonly sandbox: SandboxCredentialScope;
  readonly gatewayTokens: GatewayTokensClient;
  /** Sandbox agent access tokens — bearers external agents use against /mcp. */
  readonly agentTokens: AgentTokensClient;
  /** Sandbox Telegram inbound control — enable webhook, link codes, bindings. */
  readonly telegram: TelegramClient;

  constructor(private readonly http: BaseClient) {
    this.sandbox = new SandboxCredentialScope(http);
    this.gatewayTokens = new GatewayTokensClient(http);
    this.agentTokens = new AgentTokensClient(http);
    this.telegram = new TelegramClient(http);
  }

  async list(query: ListCredentialsQuery): Promise<Credential[]> {
    const res = await this.http.get<{ success: boolean; credentials?: Credential[]; data?: Credential[] }>(
      '/credentials',
      {
        query: {
          projectId: query.projectId,
          provider: query.provider,
          capability: query.capability,
        },
      },
    );
    return res.credentials ?? res.data ?? [];
  }

  async create(body: CreateCredentialRequest): Promise<Credential> {
    const res = await this.http.post<{ success: boolean; data?: Credential; credential?: Credential }>(
      '/credentials',
      { body },
    );
    const value = res.data ?? res.credential;
    if (!value) throw new Error('createCredential response missing body.');
    return value;
  }

  async update(credentialId: string, body: UpdateCredentialRequest): Promise<Credential> {
    const res = await this.http.put<{ success: boolean; credential?: Credential; data?: Credential }>(
      `/credentials/${credentialId}`,
      { body },
    );
    const value = res.credential ?? res.data;
    if (!value) throw new Error('updateCredential response missing body.');
    return value;
  }

  delete(credentialId: string): Promise<SimpleKeysResponse> {
    return this.http.delete<SimpleKeysResponse>(`/credentials/${credentialId}`);
  }

  /**
   * listOrgNotify returns the org-scoped notification credentials (Slack /
   * Teams / PagerDuty / SMTP, workspaceId NULL) — the channels the org owns
   * for platform-event notifications, managed on Settings → System
   * notifications. Distinct from project-scoped notify credentials.
   */
  async listOrgNotify(): Promise<Credential[]> {
    const res = await this.http.get<{ success: boolean; credentials?: Credential[]; data?: Credential[] }>(
      '/credentials/org-notify',
    );
    return res.credentials ?? res.data ?? [];
  }

  /**
   * createOrgNotify creates an org-scoped notification credential. The body is
   * the universal credential shape minus projectId/workspaceId (the server
   * pins workspaceId to NULL); provider must be a notify transport.
   */
  async createOrgNotify(
    body: Omit<CreateCredentialRequest, 'projectId' | 'workspaceId'>,
  ): Promise<Credential> {
    const res = await this.http.post<{ success: boolean; data?: Credential; credential?: Credential }>(
      '/credentials/org-notify',
      { body },
    );
    const value = res.data ?? res.credential;
    if (!value) throw new Error('createOrgNotify response missing body.');
    return value;
  }

  /**
   * updateOrgNotify applies a partial update to an org-scoped notification
   * credential. Secret is optional — omit it to keep the stored one, supply a
   * new value to rotate it. The server republishes the org topology so the
   * change reaches consumers without a refresh round-trip.
   */
  async updateOrgNotify(credentialId: string, body: UpdateCredentialRequest): Promise<Credential> {
    const res = await this.http.put<{ success: boolean; credential?: Credential; data?: Credential }>(
      `/credentials/org-notify/${credentialId}`,
      { body },
    );
    const value = res.credential ?? res.data;
    if (!value) throw new Error('updateOrgNotify response missing body.');
    return value;
  }

  deleteOrgNotify(credentialId: string): Promise<SimpleKeysResponse> {
    return this.http.delete<SimpleKeysResponse>(`/credentials/org-notify/${credentialId}`);
  }

  /**
   * adminDeliver seeds a credential into ANOTHER org's project — the
   * platform-admin delivery path for eval-sandbox credentials. The caller
   * must be a platform admin; the target org comes from the path (no
   * impersonation involved) and the audit event lands in the target org's
   * trail. The server accepts cloud (storage-capable) providers only.
   * Optional `metadata.expiresAt` (RFC3339) marks a time-bound credential
   * so the tenant UI can badge the upcoming sandbox expiry.
   */
  async adminDeliver(orgId: string, body: CreateCredentialRequest): Promise<Credential> {
    const res = await this.http.post<{ success: boolean; data?: Credential; credential?: Credential }>(
      `/admin/orgs/${orgId}/credentials`,
      { body },
    );
    const value = res.data ?? res.credential;
    if (!value) throw new Error('adminDeliver response missing body.');
    return value;
  }

  /**
   * providers fetches the server-side catalog. The tsclient ships its
   * own catalog mirror in ./providers.ts that the frontend can use
   * synchronously; this endpoint is the runtime source of truth and
   * picks up customer extensions added server-side without a tsclient
   * republish.
   */
  async providers(): Promise<ProviderSpec[]> {
    const res = await this.http.get<{ success: boolean; providers?: ProviderSpec[]; data?: ProviderSpec[] }>('/providers');
    return res.providers ?? res.data ?? [];
  }

  /**
   * globalAiProviders returns the AI provider ids the platform "global" key
   * pool (admin org's reference project) can serve, e.g. ['ANTHROPIC']. Empty
   * when no platform keys are configured — the UI then hides the non-BYOK
   * provider picker entirely (BYOK-only, as before the feature).
   */
  async globalAiProviders(): Promise<ProviderID[]> {
    const res = await this.http.get<{ success: boolean; providers?: ProviderID[]; data?: ProviderID[] }>(
      '/credentials/global-ai-providers',
    );
    return res.providers ?? res.data ?? [];
  }
}

export type { ProviderID };
